import { useEffect, useState } from 'react';
import type { IdtCaseRow, IdtCaseStatus, IdtCategory, IdtKpiResponse } from '../../../lib/api.js';
import { quarterLabelOf, quarterMonthsOf } from './periods.js';

/**
 * デザイナー個人色 (CSS変数経由でlight/dark両対応)。
 * IDT KPI ページの bullet / donut / case-table / karte で色味を共有するための SoT。
 *
 * @graph-stack product-innovation-lab
 * @graph-domain Engineering
 * @graph-business IDT KPI 描画で使うデザイナー9人の id と色変数の固定リスト
 * @graph-connects none
 */
export const DESIGNERS = [
  { id: 'seven', colorVar: '--color-designer-seven' },
  { id: 'pippin', colorVar: '--color-designer-pippin' },
  { id: 'myon', colorVar: '--color-designer-myon' },
  { id: 'abejo', colorVar: '--color-designer-abejo' },
  { id: 'guccy', colorVar: '--color-designer-guccy' },
  { id: 'seddy', colorVar: '--color-designer-seddy' },
  { id: 'yan', colorVar: '--color-designer-yan' },
  { id: 'okupi', colorVar: '--color-designer-okupi' },
  { id: 'sachi', colorVar: '--color-designer-sachi' },
] as const;

export type DesignerId = (typeof DESIGNERS)[number]['id'];

/**
 * karte (デザイン評価カルテ) を表示するデザイナーを表示段 (行) 別に定義する。
 * 1 段目 = 従来メンバー、2 段目 = 後から追加したメンバー (okupi / sachi)。
 * DesignerKarteRow が各行を重ねカードの 1 段として描画する。
 *
 * @graph-stack product-innovation-lab
 * @graph-domain Engineering
 * @graph-business karte を表示するデザイナー id を表示段 (行) 別に並べた固定リスト
 * @graph-connects none
 */
export const KARTE_DESIGNER_ROWS = [
  ['seven', 'pippin', 'myon', 'abejo', 'guccy', 'seddy', 'yan'],
  ['okupi', 'sachi'],
] as const;

export type KarteDesignerId = (typeof KARTE_DESIGNER_ROWS)[number][number];

/** 全 karte デザイナー id (段を flatten)。型判定・my-page 判定用。 @graph-connects none */
export const KARTE_DESIGNER_IDS: readonly KarteDesignerId[] = [
  ...KARTE_DESIGNER_ROWS[0],
  ...KARTE_DESIGNER_ROWS[1],
];

/**
 * 淡色 (高輝度) の個人テーマ色を持つデザイナー。個人色をベタ塗り背景に使う箇所で白字を
 * 載せるとコントラストが足りないため、これらだけ深色文字 (--color-bg-base) にする。
 * guccy=クリーム白 / abejo=高輝度の黄 / myon=淡い紫。
 *
 * @graph-stack product-innovation-lab
 * @graph-domain Engineering
 * @graph-business 個人色が淡く白字を載せられないデザイナー id の集合
 * @graph-connects none
 */
export const LIGHT_DESIGNER_IDS: ReadonlySet<string> = new Set(['guccy', 'abejo', 'myon']);

/**
 * 個人色をベタ塗り背景にしたときの前景色を返す。karte カード / karte モーダルヘッダ /
 * 絞り込みチップが同じ規則で前景色を決めるための SoT。
 *
 * @graph-stack product-innovation-lab
 * @graph-domain Engineering
 * @graph-business 個人色背景の上に載せる文字 / アイコン色 (淡色デザイナーのみ深色) を解決する
 * @graph-connects none
 */
export function onDesignerColor(id: string): string {
  return LIGHT_DESIGNER_IDS.has(id) ? 'var(--color-bg-base)' : '#ffffff';
}

/** Backlog 課題ビューの URL ベース (番号クリックで母票へ遷移)。 @graph-connects none */
export const BACKLOG_VIEW_BASE = 'https://air-closet.backlog.jp/view/';

/** ステータス → 表示色 CSS 変数名。 @graph-connects none */
export const STATUS_COLOR_VAR: Record<IdtCaseStatus, string> = {
  未対応: '--color-status-todo',
  処理中: '--color-status-inprogress',
  提出済み: '--color-status-submitted',
  完了: '--color-status-done',
};

/** ステータス → i18n キー (idtKpi.status.*)。 @graph-connects none */
export const STATUS_I18N_KEY: Record<IdtCaseStatus, string> = {
  未対応: 'notStarted',
  処理中: 'inProgress',
  提出済み: 'submitted',
  完了: 'done',
};

/**
 * 予定 pt を design_pt min/max から求める。母票の予定は min/max の中点 (平均) で、
 * 例: min=32, max=37 → 34.5。片方のみなら其の値、両方無ければ 0。
 * @graph-connects none
 */
export function plannedPt(min: number | null, max: number | null): number {
  if (min != null && max != null) return Math.round(((min + max) / 2) * 10) / 10;
  return max ?? min ?? 0;
}

/**
 * 過去 (退職 / inactive) メンバーの色。履歴回溯 (2026_3Q 等) の bullet / donut 表示用。
 * account / karte 連携は無く、静的データ表示のための色解決のみ。
 *
 * @graph-stack product-innovation-lab
 * @graph-domain Engineering
 * @graph-business 過去 IDT メンバー (nakamuu/reikoba/tamo/kid) の nickname→色変数マップ
 * @graph-connects none
 */
export const HISTORICAL_DESIGNER_COLORS: Record<string, string> = {
  // myon は 2026-10 に復帰したため DESIGNERS (現役) 側に移動済み。ここには置かない。
  nakamuu: '--color-designer-nakamuu',
  reikoba: '--color-designer-reikoba',
  tamo: '--color-designer-tamo',
  // kid は 2026-08 に IDT を離任。過去月 (2026-04/05 等) の履歴表示用に色のみ保持する。
  kid: '--color-designer-kid',
};

/** nickname(lower) → 色変数。現役デザイナー + 過去メンバーを統合した解決テーブル @graph-connects none */
const DESIGNER_COLOR_BY_ID: Record<string, string> = {
  ...Object.fromEntries(DESIGNERS.map((d) => [d.id, d.colorVar])),
  ...HISTORICAL_DESIGNER_COLORS,
};

export interface DesignerKpi {
  /** designer nickname (lower-case)。現役デザイナー以外 (過去メンバー) も入りうるため string */
  id: string;
  actual: number;
  /** actual のうち処理中 (未完了) pt。bullet tooltip の括弧表示用 */
  inProgress: number;
  currentTarget: number;
  monthlyTarget: number;
}

export interface CaseRow {
  /** 案件番号 (母票 = 親チケット issue_key)。番号リンク / IDpt算出シート解決に使う */
  issueKey: string;
  /** 子チケット issue_key (実績/ステータス編集の PATCH 対象) */
  childIssueKey: string;
  name: string;
  owner: DesignerId;
  designerTeam: string | null;
  pl: string | null;
  mainWorker: string | null;
  category: IdtCategory;
  actual: number;
  target: number;
  /** 子チケット status (未対応/処理中/提出済み/完了) */
  status: IdtCaseStatus;
  /** 子チケット status.id (Backlog 生値) */
  statusId: string | null;
}

/**
 * 担当案件明細のデフォルト表示順 (上から 提出済み > 処理中 > 未対応 > 完了)。
 * @graph-connects none
 */
export const CASE_STATUS_SORT_ORDER: Record<IdtCaseStatus, number> = {
  提出済み: 0,
  処理中: 1,
  未対応: 2,
  完了: 3,
};

export type TrendView = 'month' | 'quarter';

/**
 * API レスポンスを UI が期待する DesignerKpi[] に整形する。
 *
 * @graph-stack product-innovation-lab
 * @graph-domain Engineering
 * @graph-business IdtKpiResponse → BulletList / DonutBlock で使う DesignerKpi[] への変換
 * @graph-connects none
 */
export function toDesignerKpis(data: IdtKpiResponse | undefined): DesignerKpi[] {
  if (!data) return [];
  // API が返した designer 集合をそのまま使う (期間により現役デザイナー / 過去メンバーと変動する)。
  return data.designers.map((r) => {
    const actual = r.actualCompleted + r.actualInProgress;
    const currentTarget = Math.round(r.asOfTarget ?? 0);
    const monthlyTarget = Math.round(r.capacity ?? 0);
    return {
      id: r.nickname.toLowerCase(),
      actual: Math.round(actual),
      inProgress: Math.round(r.actualInProgress),
      currentTarget,
      monthlyTarget,
    };
  });
}

/**
 * Q view 用: 複数月 (= Q の 3 ヶ月) のレスポンスを designer 単位で合算する。
 * 月末値の合算 (target=capacity 合計 / actual=実績合計 / currentTarget=asOfTarget 合計)。
 * 月により在籍メンバーが異なるため、出現した designer を nickname で merge する。
 *
 * @graph-stack product-innovation-lab
 * @graph-domain Engineering
 * @graph-business Q view 左 KPI バレット用に 3 ヶ月分の designer KPI を合算する
 * @graph-connects none
 */
export function aggregateQuarterDesignerKpis(
  responses: (IdtKpiResponse | undefined)[],
): DesignerKpi[] {
  const acc = new Map<string, DesignerKpi>();
  const order: string[] = [];
  for (const data of responses) {
    if (!data) continue;
    for (const r of data.designers) {
      const id = r.nickname.toLowerCase();
      const actual = r.actualCompleted + r.actualInProgress;
      const cur = acc.get(id);
      if (cur) {
        cur.actual += Math.round(actual);
        cur.inProgress += Math.round(r.actualInProgress);
        cur.currentTarget += Math.round(r.asOfTarget ?? 0);
        cur.monthlyTarget += Math.round(r.capacity ?? 0);
      } else {
        order.push(id);
        acc.set(id, {
          id,
          actual: Math.round(actual),
          inProgress: Math.round(r.actualInProgress),
          currentTarget: Math.round(r.asOfTarget ?? 0),
          monthlyTarget: Math.round(r.capacity ?? 0),
        });
      }
    }
  }
  return order.map((id) => acc.get(id)).filter((d): d is DesignerKpi => d != null);
}

export interface HistoryMonthPoint {
  /** "YYYY-MM" */
  month: string;
  /** 目標 (未来月でデータ無しは null = 軸ラベルのみ空表示) */
  target: number | null;
  /** 実績 (未来月でデータ無しは null) */
  actual: number | null;
  /** その月の稼働人数 (capacity or 実績がある designer 数) */
  headcount: number;
}

export interface HistoryQuarterPoint {
  /** Q 代表月 ("YYYY-MM" = 構成月末尾) */
  representativeMonth: string;
  /** 表示ラベル (例: "2026 3Q") */
  label: string;
  /** 目標 (未来 Q でデータ無しは null) */
  target: number | null;
  /** 実績 (未来 Q でデータ無しは null) */
  actual: number | null;
}

/** その月の稼働人数 (capacity or 実績が 1 以上の designer 数) @graph-connects none */
function countActiveDesigners(data: IdtKpiResponse): number {
  return data.designers.filter(
    (d) => (d.capacity ?? 0) > 0 || d.actualCompleted > 0 || d.actualInProgress > 0,
  ).length;
}

/**
 * 履歴トレンド (月 view) 用: 各月レスポンスを月次の目標/実績/稼働人数の点に変換する。
 * 実績ロジックは上段チャートと同一 (完了 + 処理中)。月昇順にソートして返す。
 *
 * @graph-stack product-innovation-lab
 * @graph-domain Engineering
 * @graph-business 実績vs目標 履歴トレンド (月 view) の月次系列を組み立てる
 * @graph-connects none
 */
export function buildHistoryMonthPoints(
  responses: (IdtKpiResponse | undefined)[],
  currentMonth: string,
): HistoryMonthPoint[] {
  const points: HistoryMonthPoint[] = [];
  for (const data of responses) {
    if (!data) continue;
    // 未来月はデータが無ければ null (軸ラベルだけ残し線は引かない)。有れば表示する。
    const isFuture = data.month > currentMonth;
    const targetRaw = Math.round(data.team.totalCapacity);
    const actualRaw = Math.round(data.team.totalActualCompleted + data.team.totalActualInProgress);
    points.push({
      month: data.month,
      target: isFuture && targetRaw <= 0 ? null : targetRaw,
      actual: isFuture && actualRaw <= 0 ? null : actualRaw,
      headcount: countActiveDesigners(data),
    });
  }
  return points.sort((a, b) => a.month.localeCompare(b.month));
}

/**
 * 履歴トレンド (Q view) 用: 各月レスポンスを Q 単位に束ねた目標/実績の点に変換する。
 * 目標 = Q 内各月 capacity 合計。実績は上段チャートと同一ロジック:
 *   - 過去 Q: 完了のみ (quarterCompletedByDesigner 合計)
 *   - 当 Q  : 完了 + 処理中 (Q 内各月の合計)
 * Q 昇順にソートして返す。
 *
 * @graph-stack product-innovation-lab
 * @graph-domain Engineering
 * @graph-business 実績vs目標 履歴トレンド (Q view) の四半期系列を組み立てる
 * @graph-connects none
 */
export function buildHistoryQuarterPoints(
  responses: (IdtKpiResponse | undefined)[],
  currentMonth: string,
): HistoryQuarterPoint[] {
  const currentQKey = quarterMonthsOf(currentMonth)[0] ?? currentMonth;
  const groups = new Map<string, IdtKpiResponse[]>();
  for (const data of responses) {
    if (!data) continue;
    const qKey = quarterMonthsOf(data.month)[0] ?? data.month;
    const list = groups.get(qKey) ?? [];
    list.push(data);
    groups.set(qKey, list);
  }
  return [...groups.entries()]
    .sort((a, b) => a[0].localeCompare(b[0]))
    .map(([qKey, members]) => {
      const isCurrent = qKey === currentQKey;
      const isFuture = qKey > currentQKey;
      // 静的 Q は Q view 総目標 override を正とする (月別個人目標の単純合算ではない)。
      const targetOverride = members[0]?.team.quarterTargetOverride ?? null;
      const targetRaw =
        targetOverride != null
          ? Math.round(targetOverride)
          : Math.round(members.reduce((s, d) => s + d.team.totalCapacity, 0));
      let actualRaw: number;
      if (isCurrent || isFuture) {
        // 当 Q / 未来 Q: 完了 + 処理中 (上段チャートと同一)
        actualRaw = Math.round(
          members.reduce(
            (s, d) => s + d.team.totalActualCompleted + d.team.totalActualInProgress,
            0,
          ),
        );
      } else {
        // 過去 Q: 完了のみ (quarterCompletedByDesigner 合計)
        const rep = members[0];
        actualRaw = Math.round(
          rep ? Object.values(rep.team.quarterCompletedByDesigner).reduce((s, v) => s + v, 0) : 0,
        );
      }
      const last = members[members.length - 1];
      return {
        representativeMonth: last ? last.month : qKey,
        label: quarterLabelOf(qKey),
        // 未来 Q はデータ無しなら null (軸ラベルだけ空表示)
        target: isFuture && targetRaw <= 0 ? null : targetRaw,
        actual: isFuture && actualRaw <= 0 ? null : actualRaw,
      };
    });
}

/**
 * cases を持つ API レスポンス (IdtKpiResponse の月次 cases / IdtActiveCasesResponse の
 * 未完了 cases のどちらも) を CaseTable が期待する CaseRow[] に整形する。
 *
 * @graph-stack product-innovation-lab
 * @graph-domain Engineering
 * @graph-business cases 配列 (月次 or 期間非依存) → CaseTable で表示する CaseRow[] への変換
 * @graph-connects none
 */
export function toCaseRows(data: { cases: IdtCaseRow[] } | undefined): CaseRow[] {
  if (!data) return [];
  // 現役 + 過去 (離任) メンバー双方を許可する。過去 Q / 履歴月では backend が
  // activeIdtDesignersForMonth で在籍者 (離任者含む) を scope 済みで cases を返すため、
  // ここで現役 only に二次フィルタすると離任者 (kid 等) の履歴案件が明細テーブルから
  // 消え、左 KPI バレット (aggregateQuarterDesignerKpis は現役 only フィルタをしない) と
  // 数字が食い違う。未知 designer の混入だけを弾く目的なので既知 id 全体を allowlist にする。
  const allowed = new Set<string>(Object.keys(DESIGNER_COLOR_BY_ID));
  return data.cases
    .filter((c) => allowed.has(c.designer))
    .map<CaseRow>((c) => ({
      issueKey: c.issueKey,
      childIssueKey: c.childIssueKey,
      name: c.summary || c.issueKey,
      owner: c.designer as DesignerId,
      designerTeam: c.designerTeam,
      pl: c.pl,
      mainWorker: c.mainWorker,
      category: c.category,
      actual: Math.round(c.actualPt * 10) / 10,
      target: plannedPt(c.plannedPtMin, c.plannedPtMax),
      status: c.status,
      statusId: c.statusId,
    }))
    .sort(
      (a, b) =>
        CASE_STATUS_SORT_ORDER[a.status] - CASE_STATUS_SORT_ORDER[b.status] ||
        a.issueKey.localeCompare(b.issueKey),
    );
}

/**
 * 月 / 四半期 trend の mock データ生成。API データが未着のとき fallback として描画される。
 *
 * @graph-stack product-innovation-lab
 * @graph-domain Engineering
 * @graph-business 月 / 四半期 trend chart の fallback (mock) 系列を生成する
 * @graph-connects none
 */
export function buildTrendData(currentIdx: number, totalTarget: number, totalSteps: number) {
  const data: { day: number; planned: number; actual: number | null }[] = [];
  const stepTarget = totalTarget / totalSteps;
  let actualCum = 0;
  for (let i = 1; i <= totalSteps; i++) {
    const planned = Math.round(stepTarget * i);
    if (i <= currentIdx) {
      const noise = Math.sin(i * 0.7) * stepTarget * 0.25;
      const bonus = i % 3 === 0 ? stepTarget * 0.25 : 0;
      actualCum += stepTarget * 0.93 + noise + bonus;
      data.push({ day: i, planned, actual: Math.round(actualCum) });
    } else {
      data.push({ day: i, planned, actual: null });
    }
  }
  return data;
}

/**
 * DesignerId は DESIGNERS の id 由来 union なので find は必ずヒットするが、non-null assertion は
 * lint で禁止のため fallback (text-muted) を返す形にする。実害無し。
 *
 * @graph-stack product-innovation-lab
 * @graph-domain Engineering
 * @graph-business DesignerId → CSS color 変数名への解決
 * @graph-connects none
 */
export function colorVarOf(id: string): string {
  return DESIGNER_COLOR_BY_ID[id] ?? '--color-text-muted';
}

/**
 * 合計 `total` を `weights` 比で整数按分する (合計は厳密に total と一致)。端数は小数部の
 * 大きい順に +1 する。過去 Q の Q view 総目標 override を designer 別に割り振る用。
 *
 * @graph-stack product-innovation-lab
 * @graph-domain Engineering
 * @graph-business Q 総目標 override を designer 別目標に厳密整数按分する
 * @graph-connects none
 */
export function distributeIntExact(total: number, weights: number[]): number[] {
  const n = weights.length;
  if (n === 0) return [];
  const sum = weights.reduce((s, w) => s + w, 0);
  const raw = sum > 0 ? weights.map((w) => (total * w) / sum) : weights.map(() => total / n);
  const out = raw.map((r) => Math.floor(r));
  const rem = total - out.reduce((s, v) => s + v, 0);
  const order = raw.map((r, i) => ({ i, frac: r - Math.floor(r) })).sort((a, b) => b.frac - a.frac);
  for (let k = 0; k < rem && k < order.length; k++) {
    const idx = order[k]?.i;
    const cur = idx != null ? out[idx] : undefined;
    if (idx != null && cur != null) out[idx] = cur + 1;
  }
  return out;
}

/**
 * 0 → target を ease-out cubic で count up するアニメ用フック。
 *
 * @graph-stack product-innovation-lab
 * @graph-domain Engineering
 * @graph-business bullet / TeamProgressRow の % 値表示を 0 → target へアニメ補間する
 * @graph-connects none
 */
export function useCountUp(target: number, animate: boolean, duration: number, delay = 0): number {
  const [value, setValue] = useState(0);
  useEffect(() => {
    if (!animate) {
      setValue(0);
      return;
    }
    let raf = 0;
    const startTime = performance.now() + delay;
    const step = (now: number) => {
      if (now < startTime) {
        raf = requestAnimationFrame(step);
        return;
      }
      const elapsed = now - startTime;
      const progress = Math.min(1, elapsed / duration);
      const eased = 1 - Math.pow(1 - progress, 3);
      setValue(Math.round(target * eased));
      if (progress < 1) {
        raf = requestAnimationFrame(step);
      }
    };
    raf = requestAnimationFrame(step);
    return () => {
      cancelAnimationFrame(raf);
    };
  }, [target, animate, duration, delay]);
  return value;
}

/**
 * 目標線の倍率。目標は予定 (planned) のみから機械的に導出する: 目標 = 予定 × 1.05。
 *
 * @graph-connects none
 */
export const TREND_TARGET_RATIO = 1.05;

/**
 * trend 系列の各点に目標値 (= 予定 × TREND_TARGET_RATIO) を付与する。
 * 予定が引かれている点には必ず目標も付くので、月 / 四半期 / 過去期間のどの view でも
 * 目標線は予定線と同じ範囲に描かれる。
 *
 * @graph-stack product-innovation-lab
 * @graph-domain Engineering
 * @graph-business チーム予定 vs 実績 chart の目標線系列 (予定 × 1.05) を予定系列から導出する
 * @graph-connects none
 */
export function withTrendTarget<T extends { planned: number }>(
  rows: T[],
): (T & { target: number })[] {
  return rows.map((row) => ({ ...row, target: Math.round(row.planned * TREND_TARGET_RATIO) }));
}
